import type { Inquiry, PricingSeat, Campaign, CampaignDetail, Gift, GiftStatus, LineCalc, LineDefault, LineNature, Quote, QuoteStatus, Rfq, Surcharge } from './types';
import { HANDLING_FEE, LINE_DEFAULTS } from './fixtures';

/** Bỏ dấu + hạ chữ thường, để tìm "hà" ra "Hà". */
export const norm = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

// ---------- Báo giá ----------

export const QUOTE_STATUSES: readonly QuoteStatus[] = ['Nháp', 'Đã gửi', 'Khách chấp nhận', 'Hết hạn', 'Đã thành Job'];

/** Trạng thái hiển thị: quá hạn hiệu lực mà chưa thành Job thì "Hết hạn". */
export const statusOf = (q: Quote): QuoteStatus => (q.validDays < 0 && q.status !== 'Đã thành Job' ? 'Hết hạn' : q.status);

/** Chỉ báo giá khách đã chấp nhận mới có nút "Tạo job" (COST-LOGIC §8). */
export const canCreateJob = (q: Quote): boolean => statusOf(q) === 'Khách chấp nhận';

/** Tìm nhanh theo số BG, khách, tuyến, hãng (không phân biệt dấu / hoa thường). */
export function searchQuotes(rows: readonly Quote[], text: string): Quote[] {
  const n = norm(text.trim());
  return n ? rows.filter((r) => norm(`${r.id} ${r.customer} ${r.route} ${r.carrier}`).includes(n)) : [...rows];
}

export interface QuoteFilter {
  kinds?: readonly string[];
  sales?: readonly string[];
}

/** AND giữa các bộ lọc, OR trong một bộ lọc. */
export const filterQuotes = (rows: readonly Quote[], f: QuoteFilter): Quote[] =>
  rows.filter((r) => (!f.kinds?.length || f.kinds.includes(r.kind)) && (!f.sales?.length || f.sales.includes(r.sale)));

/** Chọn theo tab: 'Live' lọc báo giá Live, trạng thái thì lọc theo `statusOf`, null = tất cả. */
export const byTab = (rows: readonly Quote[], tab: QuoteStatus | 'Live' | null): Quote[] =>
  tab === null ? [...rows] : tab === 'Live' ? rows.filter((r) => r.live) : rows.filter((r) => statusOf(r) === tab);

export function statusCounts(rows: readonly Quote[]): Record<QuoteStatus, number> {
  const out = Object.fromEntries(QUOTE_STATUSES.map((s) => [s, 0])) as Record<QuoteStatus, number>;
  for (const r of rows) out[statusOf(r)]++;
  return out;
}

/** Sắp theo hiệu lực (ngày còn lại) hoặc margin. */
export function sortQuotes(rows: readonly Quote[], key: 'valid' | 'margin', dir: 'asc' | 'desc'): Quote[] {
  const k = key === 'valid' ? 'validDays' : 'margin';
  return [...rows].sort((a, b) => (dir === 'asc' ? a[k] - b[k] : b[k] - a[k]));
}

export interface QuoteStats {
  sent: number;
  accepted: number;
  /** tỉ lệ chấp nhận làm tròn %, 0 nếu chưa gửi */
  acceptRate: number;
  /** còn hiệu lực ≤ 7 ngày */
  expiringSoon: number;
}

/** Số liệu tab "Phân tích": đã gửi = mọi báo giá trừ Nháp; chấp nhận = Khách chấp nhận + Đã thành Job. */
export function quoteStats(rows: readonly Quote[]): QuoteStats {
  const sent = rows.filter((r) => statusOf(r) !== 'Nháp').length;
  const accepted = rows.filter((r) => ['Khách chấp nhận', 'Đã thành Job'].includes(statusOf(r))).length;
  return { sent, accepted, acceptRate: sent ? Math.round((accepted / sent) * 100) : 0, expiringSoon: rows.filter((r) => r.validDays >= 0 && r.validDays <= 7).length };
}

/** GP% trung bình — CHỈ gọi khi người xem có quyền `money`. */
export const avgMargin = (rows: readonly Quote[]): number => (rows.length ? rows.reduce((s, r) => s + r.margin, 0) / rows.length : 0);

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** dd/MMM/yyyy của (hôm nay + n ngày). `today` là ngày gốc theo UTC (mặc định 22/Oct/2026, ngày dữ liệu mẫu của board). */
export function dayOffset(n: number, today: Date = new Date(Date.UTC(2026, 9, 22))): string {
  const x = new Date(today.getTime() + n * 86400000);
  return `${String(x.getUTCDate()).padStart(2, '0')}/${MON[x.getUTCMonth()]}/${x.getUTCFullYear()}`;
}

/** Giá bán hiển thị: USD ≥ 100 có dấu phẩy nghìn, nhỏ hơn thì 2 số lẻ kiểu VN ("3,85"). */
export const formatPrice = (v: number): string => (v < 100 ? v.toFixed(2).replace('.', ',') : v.toLocaleString('en-US'));

// ---------- Dòng báo giá (COST-LOGIC §1) ----------

export const CALC_LABEL: Record<LineCalc, string> = { fix: 'Cố định', act: 'Theo thực tế', ahf: 'Thực tế + phí xử lý' };
export const NATURE_LABEL: Record<LineNature, string> = { svc: 'Dịch vụ HANA', obh: 'Chi hộ', inc: 'Đã bao gồm', exc: 'Không bao gồm' };

export interface LineOverride {
  calc?: LineCalc;
  nature?: LineNature;
  scope?: string;
  handlingFee?: number;
}

export interface ResolvedLine extends Required<Omit<LineOverride, 'scope'>> {
  scope: string;
  /** "Đã bao gồm" / "Không bao gồm" không có giá riêng: dòng chỉ ghi phạm vi */
  noPrice: boolean;
  priceNote: string;
}

const FALLBACK: LineDefault = { calc: 'fix', nature: 'svc' };

/** Cách tính + bản chất + phạm vi của một mã phí: mặc định theo mã, người soạn sửa đè. */
export function resolveLine(code: string, o: LineOverride = {}): ResolvedLine {
  const d = LINE_DEFAULTS[code] ?? FALLBACK;
  const calc = o.calc ?? d.calc;
  const nature = o.nature ?? d.nature;
  const handlingFee = o.handlingFee ?? HANDLING_FEE;
  const noPrice = nature === 'inc' || nature === 'exc';
  const priceNote = nature === 'inc' ? 'Đã bao gồm' : nature === 'exc' ? 'Không bao gồm' : calc === 'ahf' ? `Thực tế + ${handlingFee.toLocaleString('de-DE')} ₫` : calc === 'act' ? 'Theo thực tế' : '';
  return { calc, nature, handlingFee, scope: o.scope ?? d.scope ?? '', noPrice, priceNote };
}

/** Dòng "Đã bao gồm" bắt buộc ghi phạm vi (vd TRFA: nâng hạ cảng chuẩn trong giờ, 1 điểm, chờ 2h đầu). */
export const needsScope = (l: ResolvedLine): boolean => l.nature === 'inc' && l.scope.trim() === '';

/** Chi hộ (OBH) không được markup — chỉ thu thêm bằng dòng phí xử lý. */
export const canMarkup = (l: ResolvedLine): boolean => l.nature !== 'obh';

// ---------- Phụ thu theo biểu ----------

const price = (rows: readonly Surcharge[], key: Surcharge['key']): number => rows.find((s) => s.key === key)?.price ?? 0;

/** Kho thêm: mỗi kho thêm +800k. */
export const extraWarehouseFee = (rows: readonly Surcharge[], extra: number): number => Math.max(0, extra) * price(rows, 'wh');
/** Chờ > 2h: 200k mỗi giờ vượt (làm tròn lên). */
export const waitingFee = (rows: readonly Surcharge[], hours: number): number => Math.max(0, Math.ceil(hours - 2)) * price(rows, 'wt');
/** Lưu mooc: ⌈(h − 24) / 24⌉ × 500k khi h > 24. */
export const moocFee = (rows: readonly Surcharge[], hours: number): number => Math.max(0, Math.ceil((hours - 24) / 24)) * price(rows, 'mc');
/** Miễn thu: tổng khoản FPU / TRA / XLH dưới ngưỡng thì không thu. */
export const waiveSmall = (rows: readonly Surcharge[], amount: number): number => (amount < price(rows, 'mn') ? 0 : amount);

// ---------- Soạn báo giá: gợi ý giá bán (tiền → chỉ sau quyền money) ----------

export const MARGIN_FLOOR = 5;
export const ROUTE_MARGIN: Readonly<Record<string, number>> = { LAX: 8, LGB: 8, RTM: 7, HAM: 7, BUS: 6, SIN: 10, JEA: 8, SYD: 9 };

/** Giá bán gợi ý = giá mua × (1 + margin%), làm tròn chục. */
export const suggestSell = (buy: number, marginPct: number): number => Math.round((buy * (1 + marginPct / 100)) / 10) * 10;
export const marginOf = (sell: number, buy: number): number => (sell > 0 ? ((sell - buy) / sell) * 100 : 0);
/** Dưới sàn margin → cần duyệt giá. */
export const underFloor = (sell: number, buy: number): boolean => marginOf(sell, buy) < MARGIN_FLOOR;

// ---------- Marketing ----------

export const CAMPAIGN_STATUS_LABEL: Record<Campaign['status'], string> = { DONE: 'Đã gửi xong', SENDING: 'Đang gửi', PAUSED: 'Tạm dừng', DRAFT: 'Nháp', SCHEDULED: 'Đã lên lịch' };

const pct = (n: number, d: number): number => (d ? Math.round((n / d) * 100) : 0);
/** Tỉ lệ mở / bấm / trả lời trên số đã gửi (làm tròn %). */
export const campaignRates = (c: Campaign): { open: number; click: number; reply: number; fail: number } => ({
  open: pct(c.open, c.sent), click: pct(c.click, c.sent), reply: pct(c.reply, c.sent), fail: pct(c.fail, c.total),
});
/** Chiến dịch lỗi nhiều (> 10 mail lỗi) tô đỏ số lỗi. */
export const failHigh = (c: Campaign): boolean => c.fail > 10;
/** Khớp tổng: gửi + lỗi = tổng người nhận (chiến dịch đã xong). */
export const campaignBalanced = (c: Campaign): boolean => c.status !== 'DONE' || c.sent + c.fail === c.total;
export const campaignSalesTotal = (d: CampaignDetail): number => d.sales.reduce((s, [, n]) => s + n, 0);

export const GIFT_STATUS_LABEL: Record<GiftStatus, string> = {
  PROPOSED: 'Sale đề xuất', APPROVED: 'Đã duyệt', ORDERED: 'Đã đặt giao', SHIPPING: 'Đang giao', DELIVERED: 'Đã giao', REJECTED: 'Từ chối', FAILED: 'Giao lỗi',
};
export type GiftTab = 'all' | 'prop' | 'ship' | 'done' | 'err';
export const GIFT_TABS: Record<GiftTab, { label: string; test: (g: Gift) => boolean }> = {
  all: { label: 'Tất cả', test: () => true },
  prop: { label: 'Chờ duyệt', test: (g) => g.status === 'PROPOSED' },
  ship: { label: 'Đang giao', test: (g) => ['APPROVED', 'ORDERED', 'SHIPPING'].includes(g.status) },
  done: { label: 'Đã giao', test: (g) => g.status === 'DELIVERED' },
  err: { label: 'Lỗi / từ chối', test: (g) => ['FAILED', 'REJECTED'].includes(g.status) },
};
export const giftsByTab = (rows: readonly Gift[], tab: GiftTab): Gift[] => rows.filter(GIFT_TABS[tab].test);

/** Số đối tác đã trả lời / tổng của một RFQ. */
export const rfqProgress = (r: Rfq): { answered: number; total: number } => ({ answered: r.replies.filter((x) => x.replied).length, total: r.replies.length });

// ---------- Hàng chờ hỏi giá (Sales Desk → Pricing) ----------

/** Phiếu mở (Asked / Taken) còn ≤ 8 giờ LV là sắp quá hạn PRC. */
export const inquiryOpen = (i: Inquiry): boolean => i.status === 'Asked' || i.status === 'Taken';
export const inquiryLate = (rows: readonly Inquiry[]): number => rows.filter((i) => inquiryOpen(i) && i.hoursLeft <= 8).length;

/** Người nhận kế tiếp: ghế không nghỉ phép, ít việc mở nhất; bằng nhau thì theo thứ tự ghế. */
export function nextSeat(seats: readonly PricingSeat[]): string {
  const ok = seats.filter((s) => !s.leave).sort((a, b) => a.open - b.open);
  return ok[0]?.user ?? '';
}

/** Tự chia các phiếu Asked cho ghế ít việc nhất (mỗi phiếu cộng 1 việc mở); trả phiếu mới + danh sách "mã → người". */
export function autoAssign(rows: readonly Inquiry[], seats: readonly PricingSeat[]): { rows: Inquiry[]; done: string[] } {
  const load = new Map(seats.filter((s) => !s.leave).map((s) => [s.user, s.open]));
  const done: string[] = [];
  const out = rows.map((i) => {
    if (i.status !== 'Asked' || load.size === 0) return i;
    const [user] = [...load.entries()].sort((a, b) => a[1] - b[1])[0]!;
    load.set(user, (load.get(user) ?? 0) + 1);
    done.push(`${i.id} → ${user}`);
    return { ...i, status: 'Taken' as const, to: user };
  });
  return { rows: out, done };
}
